import "./chunkErrorHandler";
import React, { useState, useEffect } from "react";
import { render } from "react-dom";
import { BrowserRouter as Router } from "react-router-dom";
import "./index.css";
import "bootstrap/dist/css/bootstrap.css";
import "semantic-ui-css/semantic.min.css";
import App from "./App";
import { initializeSentry, withSentryBoundary } from "./sentry";
import store from "./StateManagement/store";
import { Provider } from "react-redux";
import axiosInstance from "./axios/axios";
import { Decrypt_Value } from "./MiddleWare/EncryptDecrypt";

import globe_err from "./assets/images/fluent_globe-error.png";
import { Button, Image } from "react-bootstrap";
import { message } from "antd";

message.config({
  maxCount: 1,
});

["success", "error", "info", "warning", "warn"].forEach((type) => {
  const original = message[type];

  message[type] = (...args) => {
    message.destroy();
    return original(...args);
  };
});


const root = document.getElementById("root");

// Computed once at module level — never changes during the session
const isLocalhost = Boolean(
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1" ||
  window.location.hostname.endsWith(".localhost") ||
  window.location.protocol === "http:"
);

const isProduction = Boolean(
  window.location.hostname.toLowerCase().includes("production")
);

// Special condition: Clarity and OpenReplay should not record on localhost and production
const shouldRecordSession = !isLocalhost && !isProduction;

const AppContainer = () => {
  const [apiResponse, setApiResponse] = useState(null);
  const squareAccountMode =
    localStorage.getItem("account_mode") &&
    Decrypt_Value(localStorage.getItem("account_mode"), "vozo");
  const token = localStorage.getItem("token");
  let squareScript =
    squareAccountMode == 1
      ? "https://sandbox.web.squarecdn.com/v1/square.js"
      : "https://web.squarecdn.com/v1/square.js";

  // console.log(squareScript);

  function triggerPageLoadActions() {
    loadFavicon();
    loadEssentialResources();
    loadExternalResources();
  }

  function loadScript(src) {
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = src;
      script.onload = () => {
        resolve();
      };
      script.onerror = (e) => {
        console.error("FAILED TO LOAD:", src);
        reject(e);
      };
      document.head.appendChild(script);
    });
  }

  async function loadEssentialResources() {
    try {
      // Load React FIRST
      if (!window.React) {
        await loadScript(
          "https://unpkg.com/react@17/umd/react.production.min.js",
        );
      }

      // Then ReactDOM
      if (!window.ReactDOM) {
        await loadScript(
          "https://unpkg.com/react-dom@17/umd/react-dom.production.min.js",
        );
      }

      // Then jQuery
      if (!window.jQuery) {
        await loadScript(
          "https://ajax.googleapis.com/ajax/libs/jquery/3.6.0/jquery.min.js",
        );
      }

      // The Square Script
      await loadScript(squareScript);

      // Then PageSense
      // await loadScript(
      //   "https://cdn-in.pagesense.io/js/vozo/8dafafca6bba43a5b300c7177946fded.js",
      // );
    } catch (err) {
      console.error("Script load failed:", err);
    }
  }

  // Functions to load resources as needed (same as in index.html)
  function loadFavicon() {
    const existingFavicon = document.querySelector("link[rel='icon']");
    if (existingFavicon) return;

    const favicon = document.createElement("link");
    favicon.rel = "icon";
    favicon.href = "favicon-vozo.png";
    document.head.appendChild(favicon);
  }

  //  const formioScript = document.createElement("script");
  //  formioScript.src = "https://cdn.form.io/formiojs/formio.full.min.js";
  //  document.head.appendChild(formioScript);

  // Function to load additional resources based on API response
  function loadExternalResources() {
    // Load Font Awesome CSS
    const fontAwesomeLink = document.createElement("link");
    fontAwesomeLink.rel = "stylesheet";
    fontAwesomeLink.href =
      "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css";
    document.head.appendChild(fontAwesomeLink);

    // Load Apple Touch Icon
    const appleTouchIconLink = document.createElement("link");
    appleTouchIconLink.rel = "apple-touch-icon";
    appleTouchIconLink.href = "%PUBLIC_URL%/logo192.png";
    document.head.appendChild(appleTouchIconLink);

    // const formLink = document.createElement("link");
    // formLink.rel = "stylesheet";
    // formLink.href =
    //   "https://unpkg.com/formiojs@latest/dist/formio.full.min.css";
    // document.head.appendChild(formLink);

    // const bootStrapLink = document.createElement("link");
    // bootStrapLink.rel = "stylesheet";
    // bootStrapLink.href =
    //   "https://stackpath.bootstrapcdn.com/bootstrap/4.1.3/css/bootstrap.min.css";
    // document.head.appendChild(bootStrapLink);

    // const formioLink = document.createElement("link");
    // formioLink.rel = "stylesheet";
    // formioLink.href = "https://cdn.form.io/formiojs/formio.full.min.css";
    // document.head.appendChild(formioLink);
  }

  // Function to load the Clarity tracking script lazily with type="text/javascript"
  function loadClarityTracking() {
    // Special condition: do not record on localhost and production
    if (!shouldRecordSession) {
      return;
    }

    if (window.clarity) {
      return;
    }

    window.clarity =
      window.clarity ||
      function () {
        (window.clarity.q = window.clarity.q || []).push(arguments);
      };

    const scriptElement = document.createElement("script");
    scriptElement.async = true;
    scriptElement.src = "https://www.clarity.ms/tag/nv7yzev35o";

    const firstScript = document.getElementsByTagName("script")[0];
    firstScript.parentNode.insertBefore(scriptElement, firstScript);
  }

  // isLocalhost is defined at module level (above AppContainer)

  // Function to load the OpenReplay tracking script lazily
  function loadOpenReplayTracking() {
    // Special condition: do not record on localhost and production
    if (!shouldRecordSession) {
      return;
    }

    // Helper: apply and clear user identity + metadata saved at login
    function applyPendingUserID() {
      // ── User ID: shown as session title in OpenReplay dashboard ──────────
      var pendingUser = sessionStorage.getItem("__or_pending_userid");
      if (pendingUser) {
        try {
          if (typeof window.OpenReplay.setUserID === "function") {
            window.OpenReplay.setUserID(pendingUser);
          }
        } catch (e) {}
        sessionStorage.removeItem("__or_pending_userid");
      }

      // ── Metadata: searchable/filterable fields in OpenReplay Sessions ─────
      var rawMeta = sessionStorage.getItem("__or_pending_meta");
      if (rawMeta) {
        try {
          var meta = JSON.parse(rawMeta);
          if (typeof window.OpenReplay.setMetadata === "function") {
            if (meta.provider_name) window.OpenReplay.setMetadata("provider_name", meta.provider_name);
            if (meta.facility_name) window.OpenReplay.setMetadata("facility_name", meta.facility_name);
            if (meta.facility_id)   window.OpenReplay.setMetadata("facility_id",   meta.facility_id);
            if (meta.account_type)  window.OpenReplay.setMetadata("account_type",  meta.account_type);
            if (meta.user_id)       window.OpenReplay.setMetadata("user_id",        meta.user_id);
          }
        } catch (e) {}
        sessionStorage.removeItem("__or_pending_meta");
      }
    }

    // If tracker already initialized (e.g. same-tab re-login): restart if inactive
    if (window.OpenReplay) {
      try {
        if (typeof window.OpenReplay.isActive === "function" && !window.OpenReplay.isActive()) {
          window.OpenReplay.start();
        }
        applyPendingUserID();
      } catch (err) {
        console.error("OpenReplay restart failed:", err);
      }
      return;
    }

    // First-time initialization
    var pendingUser = sessionStorage.getItem("__or_pending_userid") || "";
    var initOpts = {
      projectKey: "cYE33Y7RBAk4BymmMnnZ",
      // 0 - plain, 1 - obscured, 2 - ignored
      defaultInputMode: 0,
      obscureTextNumbers: false,
      obscureTextEmails: true,
      // Required to allow recording on localhost / HTTP
      __DISABLE_SECURE_MODE: isLocalhost,
      // Inline CSS styles so OpenReplay Cloud can render local assets
      inlineCss: isLocalhost ? 3 : 0,
    };
    // Pass the logged-in username into startOpts so the session is identified immediately
    var startOpts = { userID: pendingUser };
    (function(A,s,a,y,e,r){
      r=window.OpenReplay=[e,r,y,[s-1, e]];
      s=document.createElement('script');s.src=A;s.async=!a;
      s.onload=function(){
        // Real script has loaded — apply pending user ID then clear it
        applyPendingUserID();
      };
      document.getElementsByTagName('head')[0].appendChild(s);
      r.start=function(v){r.push([0])};
      r.stop=function(v){r.push([1])};
      r.setUserID=function(id){r.push([2,id])};
      r.setUserAnonymousID=function(id){r.push([3,id])};
      r.setMetadata=function(k,v){r.push([4,k,v])};
      r.event=function(k,p,i){r.push([5,k,p,i])};
      r.issue=function(k,p){r.push([6,k,p])};
      r.isActive=function(){return false};
      r.getSessionToken=function(){};
    })("https://static.openreplay.com/latest/openreplay.js",1,0,initOpts,startOpts);
  }

  const buttonStyle = {
    border: 0,
    borderRadius: "20px",
    boxSizing: "border-box",
    color: "#fff",
    cursor: "pointer",
    fontSize: ".875em",
    margin: 0,
    padding: "8px 16px",
    transition: "box-shadow 150ms cubic-bezier(0.4, 0, 0.2, 1)",
    background: "blue",
    userSelect: "none",
    marginTop: "50px",
  };

  useEffect(() => {
    const fetchTransferData = async () => {
      // const checkPath = "api/vozo/transfer";

      //       /api/vozo/transfer  =======> Before login page
      //        /vozo/transfer      =======> After login

      let checkPath = "api/vozo/transfer";

      // console.log(axiosInstance.defaults.baseURL);

      if (
        axiosInstance.defaults.baseURL?.endsWith("/api") &&
        checkPath.startsWith("api/")
      ) {
        checkPath = checkPath.replace(/^api\//, "");
      }

      try {
        const response = await axiosInstance.get(checkPath);
        setApiResponse(response.data);
        // Clarity and OpenReplay: NEVER record on localhost and NEVER on production
        if (parseInt(response.data) === 1 && shouldRecordSession) {
          loadClarityTracking();
          loadOpenReplayTracking();
        }

        if (parseInt(response.data) === 1 ) {
          // 👉 if user already logged in, run extra actions
          if (token) {
            triggerPageLoadActions();
          }
        }
      } catch (error) {
        console.error("API call failed:", error);
        if (isLocalhost) {
          // On localhost backend failure: show app without recording
          // (do NOT setApiResponse(0) which would block rendering with 403 page)
          setApiResponse(null);
        } else {
          setApiResponse(0);
        }
      }
    };

    fetchTransferData();
  }, []); // empty — isLocalhost is a module-level constant, not state

  if (apiResponse === 0) {
    return (
      <div
        style={{ textAlign: "left", marginTop: "50px", marginLeft: "300px" }}
      >
        <Image
          src={globe_err}
          height={70}
          width={70}
          style={{ marginBottom: "20px" }}
        ></Image>

        <h2>403 Forbidden</h2>
        <p>You do not have permission to access this application.</p>
        <Button onClick={() => window.location.reload()} style={buttonStyle}>
          Reload
        </Button>
      </div>
    );
  }

  // If API response is valid (not 0), render the main app
  return (
    // apiResponse === 1 && (
    <Provider store={store}>
      <Router>
        <App />
      </Router>
    </Provider>
    // )
  );
};

// Rendering the AppContainer instead of App directly
// render(<AppContainer />, root);
initializeSentry();
render(withSentryBoundary(AppContainer), root);
